// Управление временем раздела «Спутники»: живой режим «Сейчас» и перемотка
// момента дискретными шагами (±час/6ч/сутки/месяц) + прямой выбор момента.
// Момент трактуем в UTC — позиции спутников это пропагация TLE на инстант, а не
// локальное время оператора (см. timeControls.ts). datetime-local показывает и
// правит именно UTC-стенку, поэтому value/onChange конвертируем через UTC-части.

import { t as tAmbient, useT } from '../i18n'
import { formatLocal, type TimeStep } from './timeControls'

interface TimeToolbarProps {
  /** Выбранный момент; null — живой режим «сейчас» (позиции автообновляются). */
  selectedTime: Date | null
  /** «Сейчас»: вернуться в живой режим. */
  onNow: () => void
  /** Шаг перемотки от текущего эффективного момента (selectedTime ?? now). */
  onStep: (step: TimeStep) => void
  /** Прямой выбор момента из datetime-local (уже сконвертирован в UTC-Date). */
  onPick: (d: Date) => void
}

// Порядок кнопок шага + разделитель «прошлое | будущее» после −1ч.
const BACK_STEPS: TimeStep[] = ['-month', '-24h', '-6h', '-1h']
const FWD_STEPS: TimeStep[] = ['+1h', '+6h', '+24h', '+month']

const STEP_LABEL: Record<TimeStep, string> = {
  '-month': 'satellites.time.monthBack',
  '-24h': 'satellites.time.h24Back',
  '-6h': 'satellites.time.h6Back',
  '-1h': 'satellites.time.h1Back',
  '+1h': 'satellites.time.h1Fwd',
  '+6h': 'satellites.time.h6Fwd',
  '+24h': 'satellites.time.h24Fwd',
  '+month': 'satellites.time.monthFwd',
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Date → значение datetime-local в UTC-стенке (`YYYY-MM-DDTHH:MM`). */
function toInputValue(d: Date): string {
  return (
    `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}` +
    `T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`
  )
}

/** Значение datetime-local → Date, трактуя ввод как UTC (не локаль машины). */
function fromInputValue(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v)
  if (!m) return null
  const [, y, mo, day, h, mi] = m
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(day), Number(h), Number(mi)))
}

function StepButton({ step, onStep }: { step: TimeStep; onStep: (s: TimeStep) => void }) {
  return (
    <button
      type="button"
      onClick={() => onStep(step)}
      className="tnum rounded-md border border-hull px-1.5 py-0.5 text-[11px] text-mist transition-colors hover:border-sonar hover:text-foam"
    >
      {tAmbient(STEP_LABEL[step])}
    </button>
  )
}

export function TimeToolbar({ selectedTime, onNow, onStep, onPick }: TimeToolbarProps) {
  const t = useT()
  const live = selectedTime === null
  // База отображения/шага: выбранный момент, а в живом режиме — «сейчас».
  const base = selectedTime ?? new Date()

  return (
    <div className="pointer-events-auto flex flex-col gap-2 rounded-xl border border-hull bg-abyss/95 px-3 py-2 shadow-console backdrop-blur">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onNow}
          aria-pressed={live}
          className={[
            'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
            live
              ? 'bg-sonar/20 text-foam ring-1 ring-sonar'
              : 'border border-hull text-mist hover:border-sonar hover:text-foam',
          ].join(' ')}
        >
          {t('satellites.time.now')}
        </button>
        <span className="tnum text-xs font-medium text-foam">{formatLocal(base)}</span>
        {live && (
          <span className="text-[10px] uppercase tracking-wide text-sonar" aria-hidden>
            live
          </span>
        )}
      </div>

      <div className="flex items-center justify-center gap-1">
        {BACK_STEPS.map(s => (
          <StepButton key={s} step={s} onStep={onStep} />
        ))}
        <span className="mx-0.5 h-4 w-px bg-hull" aria-hidden />
        {FWD_STEPS.map(s => (
          <StepButton key={s} step={s} onStep={onStep} />
        ))}
      </div>

      <input
        type="datetime-local"
        value={toInputValue(base)}
        onChange={e => {
          const d = fromInputValue(e.target.value)
          if (d) onPick(d)
        }}
        aria-label={t('satellites.time.pickAria')}
        className="tnum w-full rounded-md border border-hull bg-trench px-2 py-1 text-[11px] text-foam [color-scheme:dark]"
      />
    </div>
  )
}
